import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Typography, Box, useMediaQuery, useTheme, CircularProgress } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable } from '../../../../../Components';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';
import { StepProps } from './types';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { useAptorApi } from '../../../../../Api';
import UpdateConnectionInfo from './UpdateConnectionInfo';
import { IGridQueryRequest } from '../../../../../Api/AptorApi';

interface Step6LawConnectionDeleteFormProps extends StepProps {
  lawId?: number;
  unitId?: number;
  selectedConnectionType?: SelectOption | null;
  localDeletedItems?: any[];
  onDeleteItem: (item: any) => void;
  onRemoveDeletedItem: (id: any) => void;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

const Step6LawConnectionDeleteForm = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  lawId,
  unitId,
  selectedConnectionType,
  localDeletedItems = [],
  onDeleteItem,
  onRemoveDeletedItem,
  currentStepName,
    updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step6LawConnectionDeleteFormProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { api, abortController } = useAptorApi();
  const [existingItems, setExistingItems] = useState<any[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const tableRef = useRef<any>(null);
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);

  useEffect(() => {
    if (lawId && unitId) {
      fetchExistingItems();
    }
  }, [lawId, unitId, selectedConnectionType]);

  const fetchExistingItems = useCallback(async () => {
    setIsLoadingItems(true);
    try {
      let items: any[] = [];
      const request: IGridQueryRequest = {
        page: 0,
        pageSize: 1000,
        orderBy: 'name',
        orderDirection: 0,
        search: '',
      };

      if (selectedConnectionType?.value === 'process') {
        const result = await api.searchRequirementProcesses(lawId!, unitId!, request);
        items = result?.data || [];
      } else if (selectedConnectionType?.value === 'aspect') {
        const result = await (api as any).searchRequirementAspects(lawId!, unitId!, request);
        items = result?.data || [];
      } else if (selectedConnectionType?.value === 'tag') {
        const result = await api.searchRequirementTags(lawId!, unitId!, request);
        items = result?.data || [];
      }

      if (!abortController.current.signal.aborted) {
        setExistingItems(items);
        setIsLoadingItems(false);
      }
    } catch (error) {
      if (!abortController.current.signal.aborted) {
        console.error('Failed to fetch existing items:', error);
        setExistingItems([]);
        setIsLoadingItems(false);
      }
    }
  }, [lawId, unitId, selectedConnectionType, api, abortController]);

  const getTitle = () => {
    switch (selectedConnectionType?.value) {
      case 'process':
        return formatMessage({ id: 'law-portal.manage.edit-law.connectedProcesses' });
      case 'aspect':
        return formatMessage({ id: 'law-portal.manage.edit-law.connectedAspects' });
      case 'tag':
        return formatMessage({ id: 'law-portal.manage.edit-law.connectedTags' });
      default:
        return '';
    }
  };

  const handleToggle = (item: any) => {
    const isSelected = localDeletedItems.some((i) => i.id === item.id);
    if (isSelected) {
      onRemoveDeletedItem(item.id);
    } else {
      onDeleteItem(item);
    }
  };

  const checkboxAction = (row: any) => {
    const isSelected = localDeletedItems.some((item) => item.id === row.id);
    return {
      tooltip: isSelected
        ? formatMessage({ id: 'component.mtable.unselectTooltip' })
        : formatMessage({ id: 'component.mtable.selectTooltip' }),
      icon: isSelected ? 'check_box' : 'check_box_outline_blank',
      isFreeAction: true,
      onClick: (event: any) => {
        event.stopPropagation();
        handleToggle(row);
      },
      iconProps: {
        style: { color: '#c4122f' },
      },
    };
  };

  return (

          <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            // justifyContent: 'center',
            // alignItems: 'center',
            minHeight: '350px',
          }}
        >
          <Box style={{ width: '100%' }}>
           <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontWeight: 600 }}>
          {formatMessage({ id: 'form.delete', defaultMessage: 'Delete' })} - {getTitle()}
        </Typography>

        {isLoadingItems ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(4) }}>
            <CircularProgress />
          </Box>
        ) : 
       (
        <Box style={{ maxHeight: '500px', overflow: 'auto', marginTop: theme.spacing(2) }}>
            <MTable
              key={`delete-table-${localDeletedItems.length}`}
              tableRef={tableRef}
              columns={[
                { title: formatMessage({ id: 'form.name' }), field: 'name' },
              ]}
              actions={[checkboxAction]}
              data={existingItems}
              components={{}}
            />
            {/* {localDeletedItems.length > 0 && (
              <Typography variant="caption" style={{ display: 'block', marginTop: theme.spacing(2), color: '#c62828', fontWeight: 600 }}>
                {localDeletedItems.length} item(s) selected for deletion
              </Typography>
            )} */}
          </Box>
       )
      }
          </Box>
        </div>
        <UpdateConnectionInfo
          updateConnectionInfo={updateConnectionInfo}
          currentStepName={currentStepName}
          onUpdate={setUpdateConnectionInfo}
        />
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onNext}
          nextLabel="form.next"
           nextDisabled={localDeletedItems.length === 0}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step6LawConnectionDeleteForm;
